/**
 * src/components/pdf/2024/shared.js
 *
 * Shared react-pdf UI helpers used across all 2024 Impact Report section files.
 * Import from here instead of duplicating across section files.
 */

import React from 'react';
import { View, Text, Image, Page } from '@react-pdf/renderer';
import { styles, COLORS } from './styles';

// ---------------------------------------------------------------------------
// Page-offset context — provides document-wide page numbering to PageTemplate
// ---------------------------------------------------------------------------
export const PageOffsetContext = React.createContext({ pageOffset: 0, totalDocPages: null });

// ---------------------------------------------------------------------------
// Typography helpers
// ---------------------------------------------------------------------------
export const H1 = ({ children, color }) => (
  <Text style={[styles.h1, color ? { color } : {}]}>{children}</Text>
);
export const H2 = ({ children, color }) => (
  <Text style={[styles.h2, color ? { color } : {}]}>{children}</Text>
);
export const H3 = ({ children, color }) => (
  <Text style={[styles.h3, color ? { color } : {}]}>{children}</Text>
);
export const H4 = ({ children }) => <Text style={styles.h4}>{children}</Text>;
export const H5 = ({ children }) => <Text style={styles.h5}>{children}</Text>;
export const Body = ({ children }) => <Text style={styles.body}>{children}</Text>;
export const Small = ({ children }) => <Text style={styles.small}>{children}</Text>;
export const Spacer = () => <View style={styles.spacer} />;
export const Section = ({ children }) => <View style={styles.section}>{children}</View>;

// ---------------------------------------------------------------------------
// Content helpers
// ---------------------------------------------------------------------------
export const BulletList = ({ items, style, itemStyle, bulletStyle, textStyle }) => (
  <View style={[{ marginBottom: 6, width: '100%' }, style]}>
    {items.map((item, i) => (
      <View key={i} style={[styles.listItem, itemStyle]}>
        <Text style={[styles.listBullet, bulletStyle]}>{'\u2022'}</Text>
        <Text style={[styles.listText, textStyle]}>{item}</Text>
      </View>
    ))}
  </View>
);

export const Blockquote = ({ quote, attribution, style, quoteStyle, attributionStyle }) => (
  <View style={[{ marginVertical: 8 }, style]}>
    <Text style={[styles.blockquote, quoteStyle]}>{quote}</Text>
    {attribution ? <Text style={[styles.attribution, attributionStyle]}>{attribution}</Text> : null}
  </View>
);

export const SectionCoverQuote = ({ quote, attribution, accentColor, style, attributionStyle }) => (
  <View style={[{ marginVertical: 8 }, style]}>
    <Text style={[styles.sectionCoverBlockquote, accentColor ? { borderLeftColor: accentColor } : null]}>{quote}</Text>
    {attribution ? <Text style={[styles.sectionCoverAttribution, attributionStyle]}>{attribution}</Text> : null}
  </View>
);

export const SpotlightBox = ({ label = 'Spotlight', style, children }) => (
  <View style={[styles.spotlightBox, style]}>
    <Text style={styles.spotlightLabel}>{label}</Text>
    {children}
  </View>
);

export const Footnote = ({ num, text }) => (
  <Text style={styles.footnoteText}>
    {num}. {text}
  </Text>
);

// ---------------------------------------------------------------------------
// Table helpers
// ---------------------------------------------------------------------------
export const tableStyles = {
  table: {
    borderWidth: 0.5,
    borderColor: COLORS.borderGray,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    marginVertical: 8,
  },
  row: {
    flexDirection: 'row',
  },
  headerRow: {
    backgroundColor: COLORS.lightGray,
  },
  cell: {
    borderRightWidth: 0.5,
    borderBottomWidth: 0.5,
    borderColor: COLORS.borderGray,
    padding: 4,
    fontSize: 7,
    lineHeight: 1.45,
    color: COLORS.darkGray,
  },
  headerCell: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 7,
  },
  caption: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.navy,
    marginTop: 8,
    marginBottom: 4,
  },
};

export const TableCaption = ({ children }) => (
  <Text style={tableStyles.caption}>{children}</Text>
);

// columns: [{ label, flex }]; rows: [[cell, cell, ...]] where cell is a string or React node
export const DataTable = ({ columns, rows }) => (
  <View style={tableStyles.table} wrap>
    <View style={[tableStyles.row, tableStyles.headerRow]} fixed>
      {columns.map((col, i) => (
        <View key={i} style={[tableStyles.cell, { flex: col.flex || 1 }]}>
          <Text style={tableStyles.headerCell}>{col.label}</Text>
        </View>
      ))}
    </View>
    {rows.map((row, rIdx) => (
      <View key={rIdx} style={tableStyles.row}>
        {columns.map((col, cIdx) => {
          const content = row[cIdx];
          return (
            <View key={cIdx} style={[tableStyles.cell, { flex: col.flex || 1 }]}>
              {typeof content === 'string' || typeof content === 'number' ? (
                <Text>{content}</Text>
              ) : (
                content
              )}
            </View>
          );
        })}
      </View>
    ))}
  </View>
);

export const TCell = ({ children }) => (
  <Text style={{ fontSize: 7, lineHeight: 1.45, color: COLORS.darkGray }}>{children}</Text>
);

export const TBold = ({ children }) => (
  <Text style={{ fontSize: 7, fontFamily: 'Helvetica-Bold', lineHeight: 1.45 }}>{children}</Text>
);

export const TBullets = ({ items }) => (
  <View>
    {items.map((it, i) => (
      <View key={i} style={{ flexDirection: 'row', marginBottom: 2 }}>
        <Text style={{ width: 8, fontSize: 7 }}>{'\u2022'}</Text>
        <Text style={{ flex: 1, fontSize: 7, lineHeight: 1.45, color: COLORS.darkGray }}>{it}</Text>
      </View>
    ))}
  </View>
);

// ---------------------------------------------------------------------------
// Section cover page (hero photo + colour overlay + section title)
// ---------------------------------------------------------------------------
export function SectionCover({ bgImage, accentColor, title, subtitle, quote, attribution }) {
  return (
    <Page size="LETTER" orientation='landscape' style={{ backgroundColor: COLORS.navy, padding: 0 }}>
      <Image src={bgImage} style={styles.sectionCoverBg} />
      <View
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: COLORS.navy,
          opacity: 0.55,
        }}
      />
      <View style={styles.sectionCoverOverlay}>
        <Text style={styles.sectionCoverTitle}>{title}</Text>
        {subtitle ? <Text style={styles.sectionCoverSubtitle}>{subtitle}</Text> : null}
        {quote ? <SectionCoverQuote quote={quote} attribution={attribution} accentColor={accentColor} /> : null}
      </View>
    </Page>
  );
}
